import i18n from '../i18n';

// ─── Lo que el usuario lee cuando algo falla ────────────────────────────────
// Un callable devuelve `HttpsError(code, message, details)`. `details.reason` es
// la CLAVE i18n del mensaje y `details.params` sus valores de interpolación; el
// `message` es texto técnico pensado para logs.
//
// EL FALLBACK ES LO QUE HACE POSIBLE LA MIGRACIÓN INCREMENTAL: un error que
// todavía no tiene clave se muestra exactamente como hoy. Por eso los 463
// mensajes del dominio pueden migrar de a un módulo por vez, sin coordinación y
// sin ninguna ventana en la que la app muestre peor información que antes.

interface CallableErrorDetails {
  reason?: string;
  params?: Record<string, string | number | boolean>;
}

function detailsOf(e: unknown): CallableErrorDetails | undefined {
  return (e as { details?: CallableErrorDetails } | undefined)?.details;
}

/** El código estable del error, si lo trae. Para ramificar por CAUSA sin
 *  parsear el texto (ej. abrir un diálogo distinto según el motivo). */
export function errorReason(e: unknown): string | undefined {
  return detailsOf(e)?.reason;
}

/** Los datos que el servidor adjuntó al error. Para cuando la pantalla necesita
 *  el HECHO (qué secciones faltan, qué fecha venció) y no sólo el texto: el
 *  servidor es quien lo sabe, y adivinarlo desde el estado del cliente produce
 *  mensajes que acusan a lo que no corresponde. */
export function errorParams(e: unknown): Record<string, string | number | boolean> | undefined {
  return detailsOf(e)?.params;
}

/**
 * El texto a mostrar. Resuelve `errors.<reason>` si el error trae clave; si no,
 * cae al mensaje del servidor, y en última instancia a un genérico.
 *
 * Se usa en TODA pantalla que muestre un error de callable. Antes cada una hacía
 * `errorText(e)` por su cuenta —137 sitios— lo que
 * significaba que ninguna podía beneficiarse de una traducción aunque existiera.
 */
export function errorText(e: unknown, fallback?: string): string {
  const details = detailsOf(e);
  if (details?.reason) {
    const key = `errors.${details.reason}`;
    // `i18n.exists` y NO `defaultValue: ''`. El `parseMissingKeyHandler` que
    // configuramos para las claves faltantes devuelve un texto —"⚠️ clave" en
    // desarrollo, un genérico en producción— y ese texto es TRUTHY, así que un
    // código sin traducir habría ganado por sobre el mensaje real del servidor.
    // El usuario habría perdido el error concreto a cambio de un genérico: un
    // cambio a peor escondido dentro de dos mejoras que se pisaban entre sí.
    if (i18n.exists(key, { ns: 'common' })) {
      return i18n.t(key, { ns: 'common', ...details.params });
    }
    console.warn('[errors] código sin traducción:', details.reason);
  }
  const raw = e instanceof Error ? e.message : typeof e === 'string' ? e : '';
  // `fallback` conserva el mensaje de contexto que cada pantalla ya tenía
  // ("Error al guardar", "Error al cargar"). Sin este parámetro, migrar los
  // sitios los habría degradado todos a un genérico — un cambio silencioso a
  // peor, escondido dentro de una mejora.
  return raw || fallback || i18n.t('errors.unknown', { ns: 'common' });
}
